import { useMemo, useState } from 'react';

type Identifier = string;

type SelectedState = Partial<Record<PropertyKey, boolean>>;

type ReturnType = {
  toggleAll: (newValue?: boolean) => void;
  toggleItem: (identifier: Identifier) => void;
  isSelected: (identifier: Identifier) => boolean;
  numberOfSelectedItems: number;
  selectedItems: Identifier[];
};

export function useCheckboxSelection<T, K extends keyof T>(
  items: T[],
  key: K
): ReturnType {
  const [selectedMap, setSelectedMap] = useState<SelectedState>({});

  const toggleItem: ReturnType['toggleItem'] = (identifier) => {
    setSelectedMap((prevState) => {
      return {
        ...prevState,
        [identifier]: !prevState[identifier],
      };
    });
  };

  const selectedItems = useMemo<Identifier[]>(
    () =>
      Object.entries(selectedMap).reduce((acc, [item, isSelected]) => {
        if (isSelected) {
          acc.push(item);
        }
        return acc;
      }, []),
    [selectedMap]
  );

  const numberOfSelectedItems = selectedItems.length;

  const toggleAll: ReturnType['toggleAll'] = (newValue) => {
    const shouldSelectAll =
      newValue !== undefined ? newValue : numberOfSelectedItems === 0;

    if (shouldSelectAll) {
      setSelectedMap(
        items.reduce((acc, item) => {
          const value = item[key] as unknown as string | number;
          acc[value] = true;
          return acc;
        }, {} as SelectedState)
      );
    } else {
      setSelectedMap({});
    }
  };

  const isSelected: ReturnType['isSelected'] = (identifier) =>
    Boolean(selectedMap[identifier]);

  return {
    isSelected,
    toggleAll,
    toggleItem,
    numberOfSelectedItems,
    selectedItems,
  };
}
